/**
 * Pure helpers of the Match-3 view model: the board is rendered as a list of pieces with stable ids,
 * so moves / falls can be animated with CSS transitions.
 * piece = { id, x, y, gem, special, pop?, drop?, born? }
 */

export function cellGem(cell) {
  if (!cell) return null;
  return typeof cell === 'string' ? cell : cell.gem || null;
}

export function cellSpecial(cell) {
  return cell && typeof cell === 'object' ? cell.special || null : null;
}

let seq = 0;
export const newPieceId = () => {
  seq += 1;
  return `p${seq}`;
};

/** Pieces from a server board; ids are reused from `prev` pieces at the same cell with the same look. */
export function boardToPieces(board, prev = []) {
  const byCell = new Map();
  (prev || []).forEach((p) => { if (!p.pop) byCell.set(`${p.x},${p.y}`, p); });
  const out = [];
  (board || []).forEach((row, y) => {
    (row || []).forEach((cell, x) => {
      const gem = cellGem(cell);
      if (!gem) return;
      const special = cellSpecial(cell);
      const old = byCell.get(`${x},${y}`);
      const id = old && old.gem === gem && (old.special || null) === special ? old.id : newPieceId();
      out.push({ id, x, y, gem, special });
    });
  });
  return out;
}

export const pieceAt = (pieces, x, y) => pieces.find((p) => p.x === x && p.y === y && !p.pop) || null;

export const isAdjacent = (a, b) => !!a && !!b && Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]) === 1;

/** Board (rows of codes) from pieces — used by tests to compare with the server board. */
export function piecesToBoard(pieces, width, height) {
  const rows = Array.from({ length: height }, () => Array.from({ length: width }, () => null));
  pieces.forEach((p) => {
    if (p.pop || p.y < 0 || p.y >= height || p.x < 0 || p.x >= width) return;
    rows[p.y][p.x] = p.special ? { gem: p.gem, special: p.special } : p.gem;
  });
  return rows;
}

/** Thresholds [T, 1.5T, 2T] of a state (falls back to target). */
export function thresholdsOf(state) {
  if (Array.isArray(state?.thresholds) && state.thresholds.length === 3) return state.thresholds;
  const t = Number(state?.target) || 0;
  return [t, Math.round(t * 1.5), t * 2];
}

export function starsFor(score, thresholds) {
  return thresholds.reduce((n, t) => (t > 0 && score >= t ? n + 1 : n), 0);
}

/**
 * Would the server accept this swap? Mirrors Match3Engine.swap: adjacent non-empty cells; any swap with a
 * RAINBOW orb or of two specials is always legal; otherwise the swap must make a line of 3+ of one colour.
 * Used to bounce invalid swaps locally (no request, no 422).
 */
export function swapMakesMatch(board, a, b) {
  if (!board || !isAdjacent(a, b)) return false;
  const h = board.length;
  const w = (board[0] || []).length;
  const at = (x, y) => (x >= 0 && y >= 0 && x < w && y < h ? board[y][x] : null);
  const ca = at(a[0], a[1]);
  const cb = at(b[0], b[1]);
  if (!cellGem(ca) || !cellGem(cb)) return false;
  const sa = cellSpecial(ca);
  const sb = cellSpecial(cb);
  if (sa === 'RAINBOW' || sb === 'RAINBOW' || (sa && sb)) return true;
  const color = (x, y) => {
    let c;
    if (x === a[0] && y === a[1]) c = cb;
    else if (x === b[0] && y === b[1]) c = ca;
    else c = at(x, y);
    const g = cellGem(c);
    return g && g !== 'RAINBOW' ? g : null;
  };
  const lineAt = (x, y) => {
    const g = color(x, y);
    if (!g) return false;
    let hz = 1;
    for (let i = x - 1; color(i, y) === g; i -= 1) hz += 1;
    for (let i = x + 1; color(i, y) === g; i += 1) hz += 1;
    if (hz >= 3) return true;
    let vt = 1;
    for (let j = y - 1; color(x, j) === g; j -= 1) vt += 1;
    for (let j = y + 1; color(x, j) === g; j += 1) vt += 1;
    return vt >= 3;
  };
  return lineAt(a[0], a[1]) || lineAt(b[0], b[1]);
}
